import React, {ReactElement, ReactNode} from "react";
import {act, render, renderHook, RenderOptions} from "@testing-library/react";
import {AuthProvider, InitAccountFunc} from "../components/auth/AuthContext";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {MockAuth} from "./firebase";
import {Auth} from "../components/auth/types";
import {OAuthStore} from "../components/auth/store";
import {OAuthProvider} from "firebase/auth";

type AuthOptions = {
  auth?: Auth,
  initAccount?: InitAccountFunc,
}

export const getAuthWrapper = (opts?: AuthOptions) => {
  const auth = opts?.auth || new MockAuth();
  const initAccount = opts?.initAccount || jest.fn().mockResolvedValue(undefined);
  const AuthWrapper = ({children}: { children: React.ReactNode }) => {
    return (
        <BrowserRouter>
          <AuthProvider auth={auth} initAccount={initAccount}>{children}</AuthProvider>
        </BrowserRouter>
    )
  };
  return AuthWrapper
}

export const renderWithRouter = (elem: ReactElement, renderOpts: RenderOptions = {}) => {
  return render(<BrowserRouter>{elem}</BrowserRouter>, renderOpts);
}

export const withErrorRoutes = (elem: ReactNode, path: string) => {
  return <Routes>
    <Route path={path} element={elem}/>
    <Route path="/error" element={<p>error</p>}/>
    <Route path="*" element={<p>not found</p>}/>
  </Routes>
}

export const renderWithAct = async (elem: ReactElement | JSX.Element, renderOpts: RenderOptions = {}) => {
  let renderResult: any;
  await act ( async () => {
    renderResult = render(elem, renderOpts);
  });
  return renderResult;
}

export const renderRouterWithAct = async (elem: ReactElement, renderOpts: RenderOptions = {}) => {
  return renderWithAct(<BrowserRouter>{elem}</BrowserRouter>, renderOpts);
}

export const renderHookWithAct = async (hookFn: any, renderOpts: RenderOptions = {}) => {
  let renderResult: any;
  await act(async () => {
    renderResult = renderHook(hookFn, renderOpts);
  });
  return renderResult;
}

export const runFnWithAct = async (fn: any) => {
  return act(() => fn());
}

export const runAsyncFnWithAct = async (fn: any) => {
  return act(async () => {
    return await fn();
  });
}

export const navToPath = (path: string) => {
  window.history.pushState({}, "Test page", path);
}

export const mockWindowAssign = () => {
  const location = window.location
  const mockedLocation = {
    ...location,
    assign: jest.fn(),
  }
  jest.spyOn(window, "location", "get").mockReturnValue(mockedLocation);
  return mockedLocation.assign
};

export const mockWindowOpener = (postMessageMock: jest.Mock) => {
  if (!window.opener) {
    Object.defineProperty(window, "opener",  {
      value: {
        postMessage: postMessageMock,
      },
    });
  }
  window.opener.postMessage = postMessageMock;
};

export const givenOAuthSession = () => {
  const sessionId = "test-session-s001";
  OAuthStore.setSession(sessionId);
  OAuthStore.setRedirectUri("http://fakelet.com?code=whatever");
}

export const clearOAuthSession = () => OAuthStore.clear();

export const wait = (timeoutMillis: number) => new Promise(resolve => setTimeout(resolve, timeoutMillis))

export function waitAtLeast<T>(promise: Promise<T>, timeoutMillis: number): Promise<T> {
  return Promise.all([promise, wait(timeoutMillis)]).then(([result]) => result);
}

export function resolveAfter<T>(result: T, timeoutMillis: number): Promise<T> {
  return waitAtLeast(Promise.resolve(result), timeoutMillis);
}

export function givenMockOAuthCredential(providerId?: string, token?: string) {
  return OAuthProvider.credentialFromJSON({
    providerId: providerId || "google.com",
    signInMethod: providerId || "google.com",
    accessToken: token || "access-token-value",
    idToken: token || "id-token-value",
  });
}
